import React, { useState, useEffect } from 'react';
import { tenantApi, formatApiErrorDetail, useAuth } from '../../lib/api';
import { AppLayout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Loader2, Building2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { getStatusColor } from '../../lib/utils';

export default function TenantProfilePage() {
  const { user, checkAuth } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    owner_name: '',
    phone: '',
    address: '',
    gstin: '',
  });

  useEffect(() => {
    const fetchTenant = async () => {
      try {
        const { data } = await tenantApi.get(user.tenant_id);
        setTenant(data);
        setFormData({
          company_name: data.company_name || '',
          owner_name: data.owner_name || '',
          phone: data.phone || '',
          address: data.address || '',
          gstin: data.gstin || '',
        });
      } catch (error) {
        toast.error('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };
    if (user?.tenant_id) fetchTenant();
  }, [user]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await tenantApi.update(user.tenant_id, formData);
      toast.success('Profile updated successfully');
      // Refresh tenant data and auth state (so sidebar shows updated name)
      const { data } = await tenantApi.get(user.tenant_id);
      setTenant(data);
      await checkAuth();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Shop Profile</h1>
          <p className="text-slate-600 mt-1">Update your business information</p>
        </div>

        {/* Status Card */}
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center">
                <Building2 className="h-7 w-7 text-[#005DE8]" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg text-slate-900">{tenant?.company_name}</h3>
                <p className="text-sm text-slate-500">{tenant?.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={`${getStatusColor(tenant?.status)} border`}>{tenant?.status}</Badge>
                <Badge variant="outline" className="border-slate-200">{tenant?.contract_type}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Edit Form */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit' }}>Business Details</CardTitle>
            <CardDescription>Update your company details, GST number, and contact information</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    required
                    data-testid="profile-company-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="owner_name">Owner Name</Label>
                  <Input
                    id="owner_name"
                    value={formData.owner_name}
                    onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                    required
                    data-testid="profile-owner-name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    data-testid="profile-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gstin">GSTIN</Label>
                  <Input
                    id="gstin"
                    value={formData.gstin}
                    onChange={(e) => setFormData({ ...formData, gstin: e.target.value })}
                    placeholder="e.g., 27ENGPA3125K1Z5"
                    data-testid="profile-gstin"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Full business address"
                  data-testid="profile-address"
                />
              </div>
              <div className="flex justify-end pt-2">
                <Button type="submit" className="bg-[#005DE8] hover:bg-[#0047B3]" disabled={saving} data-testid="profile-save-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Changes
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
